/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";

import { LIVE } from "@src/components/modules/LiveModule/liveUtils";
import useWidth from "@src/components/modules/LiveModule/useWidth";

/*
 * A small line with a soft area under it (the Ops Console KPI sparklines). It stretches to the
 * width it is given; the last point gets a dot so a live line reads as "now".
 */
type Props = {
  values: number[];
  width?: number;
  height?: number;
  colour?: string;
  /** Draw the last point as a dot. */
  dot?: boolean;
  /** Step line (counts) instead of a smooth one. */
  step?: boolean;
  label?: string;
  min?: number;
};

const Sparkline = (props: Props) => {
  const [ref, measured] = useWidth(props.width || 120);
  return (
    <div ref={ref} style={{ width: props.width ? `${props.width}px` : "100%", height: props.height || 28 }}>
      <SparkSvg {...props} width={props.width || measured} />
    </div>
  );
};

const SparkSvg = ({
  values,
  width = 120,
  height = 28,
  colour = LIVE.cyan,
  dot = true,
  step = false,
  label,
  min,
}: Props) => {
  const gid = `sp${React.useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const pts = values.length === 1 ? [values[0], values[0]] : values;
  if (!pts.length) {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        <line x1={0} x2={width} y1={height - 2} y2={height - 2} stroke={LIVE.border} strokeWidth={1.5} />
      </svg>
    );
  }
  const lo = min != null ? min : Math.min(...pts);
  const hi = Math.max(...pts);
  const span = hi - lo || 1;
  const pad = 3;
  const x = (i: number) => (i / (pts.length - 1)) * (width - pad * 2) + pad;
  const y = (v: number) => height - pad - ((v - lo) / span) * (height - pad * 2);
  let d = "";
  if (step) {
    pts.forEach((v, i) => {
      d += i === 0 ? `M ${x(i)} ${y(v)}` : ` H ${x(i)} V ${y(v)}`;
    });
  } else {
    pts.forEach((v, i) => {
      if (i === 0) {
        d = `M ${x(0)} ${y(v)}`;
        return;
      }
      const px = x(i - 1);
      const py = y(pts[i - 1]);
      const cx = (px + x(i)) / 2;
      d += ` C ${cx} ${py}, ${cx} ${y(v)}, ${x(i)} ${y(v)}`;
    });
  }
  const area = `${d} L ${x(pts.length - 1)} ${height} L ${x(0)} ${height} Z`;
  const lastX = x(pts.length - 1);
  const lastY = y(pts[pts.length - 1]);
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={colour} stopOpacity={0.22} />
          <stop offset="1" stopColor={colour} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={colour} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
      {dot ? <circle cx={lastX} cy={lastY} r={2.6} fill={colour} stroke="var(--t9-bg-surface)" strokeWidth={1.2} /> : null}
    </svg>
  );
};

export default Sparkline;
